import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Copy, RefreshCw, Facebook, Instagram, Music2, ChevronLeft, Sparkles } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { copyTextBeforeNav, openOwnComposer, openCopyLinkPage, withPlatformTags } from "@/components/engage/SocialPostHelper";
import { useShareStreak } from "@/hooks/useShareStreak";
import ExtraPhotosControls from "@/features/customerWizard/ExtraPhotosControls";

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

const TEAL = "#0E7C86";

/** Iter 688 — Google is ALWAYS the 4th tile in the 2×2 grid. */
const SHARE_CARDS = [
  { key: "facebook", name: "Facebook" },
  { key: "instagram", name: "Instagram" },
  { key: "tiktok", name: "TikTok" },
  { key: "google", name: "Google review" },
];

const PLATFORM_NAMES = { facebook: "Facebook", instagram: "Instagram", tiktok: "TikTok", google: "Google" };

/** Iter 689 §3 — multicolour Google "G" (lucide has no brand G). */
function GoogleGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z" />
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.8-3.8H1.2v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.2a12 12 0 0 0 0 10.8l4.1-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.2 6.6l4.1 3.1A7.2 7.2 0 0 1 12 4.8z" />
    </svg>
  );
}

/** Iter 680/691 — brand glyph squares on the tiles (no emoji). */
function TileGlyph({ k, used }) {
  const box = "inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0";
  if (used) {
    return (
      <span className={box} style={{ backgroundColor: "#E2E8F0" }}>
        {k === "facebook" && <Facebook className="w-5 h-5" style={{ color: "#64748B" }} fill="#64748B" strokeWidth={0} />}
        {k === "instagram" && <Instagram className="w-5 h-5" style={{ color: "#64748B" }} />}
        {k === "tiktok" && <Music2 className="w-5 h-5" style={{ color: "#64748B" }} />}
        {k === "google" && <span className="text-lg font-black" style={{ color: "#64748B" }}>G</span>}
      </span>
    );
  }
  if (k === "facebook")
    return (
      <span className={box} style={{ backgroundColor: "#1877F2" }}>
        <Facebook className="w-5 h-5 text-white" fill="#FFFFFF" strokeWidth={0} />
      </span>
    );
  if (k === "instagram")
    return (
      <span className={`${box} bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]`}>
        <Instagram className="w-5 h-5 text-white" />
      </span>
    );
  if (k === "tiktok")
    return (
      <span className={box} style={{ backgroundColor: "#0F172A" }}>
        <Music2 className="w-5 h-5 text-white" />
      </span>
    );
  // Iter 688 — white rounded square with the bold Google "G".
  return (
    <span className={`${box} bg-white border border-slate-200`}>
      <GoogleGlyph className="w-5 h-5" />
    </span>
  );
}

// Iter 675 — only real social-post / review URLs may auto-paste from the
// clipboard; our own caption, random text and other sites are ignored.
const SOCIAL_URL_RE =
  /^https?:\/\/([a-z0-9-]+\.)?(facebook\.com|fb\.com|fb\.watch|instagram\.com|tiktok\.com|vm\.tiktok\.com|google\.com|g\.page|goo\.gl|maps\.app\.goo\.gl)\//i;

// Iter 680/689 — per-platform "how to copy the link" step.
const HOWTO_SHORT = {
  facebook: "open your post → ⋯ → Copy link",
  instagram: "open your post → ⋯ → Copy link",
  tiktok: "Share → Copy link",
  google: "open your review → Share → Copy link",
};

/**
 * Iter 690/691 — STAGED share step (founder's 6-card mock):
 * stage 1 "Share your photo" (photo hero → caption card → 2×2 tiles →
 * Open {Platform}) · stage 2 "Post to {Platform}" (3-step card → link
 * input → Submit link). Tile tap = select, primary button = open; the
 * open advances to stage 2; ‹ Back returns. Google (Iter 688) is always
 * the 4th tile — review link if set, else Maps search on the store.
 */
export default function ShareStep({
  sessionId,
  photo,
  onRetake,
  socialLinks,
  vendor,
  platforms,
  onProofDone,
  onSkip,
  extraPhotos = null,
  onAddExtra,
  onMakeCollage,
  collageBusy = false,
}) {
  const links = socialLinks || {};
  const pInfo = (key) => (platforms || []).find((p) => p.platform === key) || null;
  // Iter 688 — Maps fallback so the Google tile ALWAYS works.
  const mapsUrl =
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent([vendor?.business_name, vendor?.physical_address].filter(Boolean).join(" ") || "my store");
  const googleUrl = links.google_review_url || pInfo("google")?.url || mapsUrl;

  const [used, setUsed] = useState(() => new Set());
  const [selected, setSelected] = useState("facebook");
  const [stage2, setStage2] = useState(false);
  const [url, setUrl] = useState("");
  const [captions, setCaptions] = useState(null);
  const [capBusy, setCapBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Iter 675/676 — clipboard auto-paste + 3 s auto-submit countdown.
  const sharedOutRef = useRef(false);
  const urlRef = useRef("");
  const [pasteHint, setPasteHint] = useState(null); // {tone:'ok'|'warn', text}
  const [countdown, setCountdown] = useState(null); // 3 → 2 → 1 → submit

  const setUrlTracked = (v) => {
    urlRef.current = v;
    setUrl(v);
  };

  // Iter 675 §3 — first whitespace-separated clipboard token, accepted only
  // when it matches the social-post allow-list.
  const applyClipboard = async (manual) => {
    try {
      const text = await navigator.clipboard.readText();
      const token = (text || "").trim().split(/\s+/)[0] || "";
      if (SOCIAL_URL_RE.test(token)) {
        setUrlTracked(token.slice(0, 500));
        if (manual) {
          setPasteHint({ tone: "ok", text: "✅ Post link pasted — tap Submit link." });
        } else {
          // Iter 676 §A — auto path starts the 3 s auto-submit countdown.
          setPasteHint({ tone: "ok", text: "✅ Post link pasted automatically." });
          setCountdown(3);
        }
        return;
      }
      if (manual) {
        setPasteHint({
          tone: "warn",
          text: "📋 No post link on the clipboard yet — in the app, open your post → Share → Copy link, then come back.",
        });
      }
    } catch {
      if (manual) {
        setPasteHint({ tone: "warn", text: "📋 Couldn't read the clipboard — paste the link into the box below." });
      }
    }
  };

  // Iter 675 §2 — on return to the tab (armed + empty field) try the
  // clipboard silently.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (!sharedOutRef.current || urlRef.current) return;
      applyClipboard(false);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelAutoSubmit = () => {
    setCountdown(null);
    setPasteHint({ tone: "warn", text: "Auto-submit cancelled — tap Submit link when ready." });
  };

  // Iter-586 — server-side share streak.
  const streakQ = useShareStreak();

  // Caption ideas — fetched once on entry, never gating.
  const fetchSuggestions = async () => {
    setCapBusy(true);
    try {
      const body = { platform: selected === "google" ? "instagram" : selected };
      if (photo?.b64) body.photo_b64 = photo.b64;
      const { data } = await sharedApi.post(
        `/classifieds/engagement/sessions/${sessionId}/post-suggestions`,
        body,
        OPTS,
      );
      setCaptions(data?.suggestions || []);
      setCopiedIdx(null);
    } catch {
      // Silent — the customer can still write their own caption.
    } finally {
      setCapBusy(false);
    }
  };
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current || !sessionId) return;
    fetchedRef.current = true;
    fetchSuggestions();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCaption = (copiedIdx != null && captions?.[copiedIdx]) || captions?.[0] || null;
  const vendorTags = links.tags || {};
  const allsaleTag = links.allsale_tag || "@allsale";
  const activeTagKey = selected === "google" ? "instagram" : selected;
  const taggedCaption = (text, key) => withPlatformTags(text || "", key, vendorTags, allsaleTag);
  const shownCaption = taggedCaption(pendingCaption, activeTagKey);

  const copyCaption = (text) => {
    if (copyTextBeforeNav(text)) {
      setCaptionCopied(true);
      toast.success("Copied!");
    } else {
      toast.error("Couldn't copy — long-press the text instead");
    }
  };

  /** Iter 691 — tile tap = SELECT; the primary "Open {Platform}" opens. */
  const openComposer = () => {
    sharedOutRef.current = true; // Iter 675 — arm the on-return clipboard read
    if (selected === "google") {
      window.open(googleUrl, "_blank", "noopener");
    } else {
      const caption = shownCaption;
      if (caption && copyTextBeforeNav(caption)) {
        setCaptionCopied(true);
        toast.success("Caption copied — paste it into your post.");
      }
      openOwnComposer(selected, caption, vendor?.share_url);
    }
    setUsed((prev) => new Set(prev).add(selected));
    setStage2(true);
  };

  const submit = async (withPhoto) => {
    setCountdown(null); // Iter 676 — any submit kills a pending countdown
    setSubmitting(true);
    try {
      const body = { platform: String(selected).toLowerCase() };
      if (url.trim()) body.post_url = url.trim().slice(0, 500);
      if (withPhoto && photo?.b64) {
        if (photo.b64.length > 3_200_000) {
          toast.error("Photo too large (max ~3MB) — please choose a smaller one.");
          setSubmitting(false);
          return;
        }
        body.screenshot_b64 = photo.b64;
      }
      const { data } = await sharedApi.post(
        `/classifieds/engagement/sessions/${sessionId}/post`,
        body,
        // Iter 700 Addendum D §2 — 15 s client timeout so a stalled API
        // proxy can never leave the submit button disabled forever.
        { ...OPTS, timeout: 15000 },
      );
      const verified = data?.status === "verified" || data?.verified === true;
      // Iter 664 — the post-submit message comes from the API.
      toast.success(
        data?.message ||
          (verified
            ? "Post verified — discount unlocked."
            : "Sending your post… The store will approve it — automatically within 48 hours if they don't respond."),
      );
      try {
        const fresh = await streakQ.refetch();
        const st = fresh?.data;
        if (st?.milestone_hit) {
          toast.success(`🎉 Milestone! ${st.posts_total} posts shared — you're on fire!`, { duration: 6000 });
        }
      } catch {
        // streak is a nudge, never a blocker
      }
      onProofDone(verified ? "verified" : "pending");
    } catch (err) {
      // Iter 700 Addendum D §2 — timeout / network drop: re-enable and
      // coach a retry instead of a dead grey button.
      if (err?.code === "ECONNABORTED" || !err?.response) {
        toast.error("Couldn't reach the server — tap to retry.");
        return;
      }
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d.trim() ? d : "Couldn't submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Iter 676 §A — tick 3 → 2 → 1, then submit the URL.
  useEffect(() => {
    if (countdown == null) return undefined;
    if (countdown <= 0) {
      setCountdown(null);
      submit(false);
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => (c == null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const platformName = PLATFORM_NAMES[selected] || "the app";
  const hasUrl = !!url.trim();

  /* ---------- shared footer (muted text links, 690 §5) ---------- */
  const footer = (
    <div className="pt-1 flex flex-col items-center gap-2">
      <button
        type="button"
        data-testid={stage2 ? "cust-submit-nolink" : "cust-upload-pic"}
        disabled={submitting}
        onClick={() => submit(true)}
        className="min-h-[44px] text-xs font-bold text-slate-500 underline hover:text-slate-800 disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Submit without link — owner verifies"}
      </button>
      {onSkip && (
        <button
          type="button"
          data-testid="cust-skip-review-share"
          onClick={onSkip}
          className="min-h-[44px] text-xs font-bold text-slate-500 underline hover:text-slate-800"
        >
          ⏭ Skip for 48 hrs — pay full price now (+$0.20 service fee, back as 20 pts if you post)
        </button>
      )}
    </div>
  );

  /* =================== STAGE 2 — Post to {Platform} =================== */
  if (stage2) {
    return (
      <div className="space-y-3" data-testid="cust-share-stage-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="cust-share-back"
            onClick={() => setStage2(false)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-[17px] font-black text-slate-900">Post to {platformName}</h3>
        </div>

        {/* Iter 689 §2 / 690 — 3-step card; steps 2–3 turn ✓ with a link. */}
        <div
          className="rounded-[14px] border p-3.5 space-y-2.5 text-xs text-slate-700 leading-relaxed"
          style={{ backgroundColor: "#0E7C860D", borderColor: "#0E7C8633" }}
          data-testid="cust-copy-link-step"
        >
          {[
            { done: true, text: "Caption copied — paste it into your post." },
            { done: hasUrl, text: `In ${platformName}: ${HOWTO_SHORT[selected] || HOWTO_SHORT.facebook}, then come back.` },
            { done: hasUrl, text: "We paste the link for you and submit it automatically." },
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black shrink-0 mt-px"
                style={
                  step.done
                    ? { backgroundColor: TEAL, color: "#fff" }
                    : { backgroundColor: "#fff", color: TEAL, border: `1.5px solid ${TEAL}` }
                }
              >
                {step.done ? "✓" : i + 1}
              </span>
              <span className="flex-1">{step.text}</span>
            </div>
          ))}
        </div>

        {/* Primary — hidden once a link is in the box (690 §3). */}
        {!hasUrl && (
          <button
            type="button"
            data-testid="cust-open-to-copy-link"
            onClick={() => {
              sharedOutRef.current = true;
              openCopyLinkPage(selected);
            }}
            className="w-full min-h-[52px] rounded-full text-white text-sm font-black shadow-card transition-opacity hover:opacity-90"
            style={{ backgroundColor: TEAL }}
          >
            📲 Open {platformName} to copy your link
          </button>
        )}

        {/* Link input with embedded Paste (690 §4). */}
        <div className="relative">
          <input
            value={url}
            onChange={(e) => {
              if (countdown != null) cancelAutoSubmit();
              setUrlTracked(e.target.value);
            }}
            placeholder="Post link (auto-filled when you come back)"
            inputMode="url"
            maxLength={500}
            data-testid="cust-post-url"
            className="w-full rounded-full bg-white border border-slate-200 focus:border-slate-400 pl-4 pr-24 py-3 text-xs focus:outline-none"
          />
          <button
            type="button"
            data-testid="cust-paste-link"
            onClick={() => applyClipboard(true)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold px-3.5 py-2 transition-colors"
          >
            📋 Paste
          </button>
        </div>
        {pasteHint && (
          <p
            data-testid="cust-post-hint"
            className={`text-xs font-semibold ${pasteHint.tone === "ok" ? "text-emerald-700" : "text-amber-700"}`}
          >
            {pasteHint.text}
          </p>
        )}
        {countdown != null && (
          <button
            type="button"
            data-testid="cust-auto-submit"
            onClick={cancelAutoSubmit}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-black py-2.5 transition-colors hover:bg-amber-200"
          >
            ⏱ Submitting in {countdown}s — tap to cancel
          </button>
        )}

        {/* Submit link — only when a link is present (690 §5). */}
        {hasUrl && (
          <button
            type="button"
            data-testid="cust-submit-url"
            disabled={submitting}
            onClick={() => submit(false)}
            className="w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-full text-white text-sm font-black shadow-card transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: TEAL }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit link ✓"}
          </button>
        )}

        {/* Inline pills (690 §6). */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="cust-reopen-composer"
            onClick={openComposer}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2.5 transition-colors"
          >
            ↻ Haven&apos;t posted yet? Open {platformName}
          </button>
          <button
            type="button"
            data-testid="cust-paste-link-2"
            onClick={() => applyClipboard(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-xs font-bold px-3.5 py-2.5 transition-colors"
          >
            📋 I copied it — paste
          </button>
        </div>

        {!hasUrl && footer}
      </div>
    );
  }

  /* =================== STAGE 1 — Share your photo =================== */
  return (
    <div className="space-y-3" data-testid="cust-share-stage-1">
      <div>
        <h3 className="text-[17px] font-black text-slate-900">Where will you share?</h3>
        <p className="mt-0.5 text-xs text-slate-500">
          Tap a platform — your caption is copied and the app opens on a new post.
        </p>
      </div>

      {/* Photo hero 4:3, overlay pills (690 §2). */}
      {photo?.previewUrl ? (
        <div>
          <div className="relative">
            <img
              src={photo.previewUrl}
              alt="Your visit"
              className="w-full aspect-[4/3] object-cover rounded-[20px] border border-slate-200"
              data-testid="engage-photo-preview"
            />
            <div className="absolute top-2.5 right-2.5 flex gap-1.5">
              <button
                type="button"
                data-testid="cust-retake"
                onClick={onRetake}
                className="inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur text-white text-[11px] font-bold px-3 py-1.5 hover:bg-black/70 transition-colors"
              >
                <RefreshCw className="w-3 h-3" /> Retake
              </button>
              {onAddExtra && (
                <label
                  data-testid="cust-add-photos"
                  className="inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur text-white text-[11px] font-bold px-3 py-1.5 hover:bg-black/70 transition-colors cursor-pointer"
                >
                  ＋ Add
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      // onAddExtra expects {b64, previewUrl} (Iter-627).
                      const previewUrl = URL.createObjectURL(f);
                      const reader = new FileReader();
                      reader.onload = () => {
                        const b64 = String(reader.result || "").split(",")[1] || "";
                        if (b64) onAddExtra({ b64, previewUrl });
                      };
                      reader.readAsDataURL(f);
                    }}
                  />
                </label>
              )}
            </div>
          </div>
          {onAddExtra && extraPhotos?.length > 0 && (
            <ExtraPhotosControls
              extraPhotos={extraPhotos}
              onAddExtra={onAddExtra}
              onMakeCollage={onMakeCollage}
              collageBusy={collageBusy}
            />
          )}
        </div>
      ) : (
        <div
          className="w-full aspect-[4/3] rounded-[20px] border-2 border-dashed border-slate-300 bg-slate-50 grid place-items-center text-center px-6"
          data-testid="cust-photo-empty"
        >
          <p className="text-xs font-semibold text-slate-500">📷 No photo yet — add one to boost your post</p>
        </div>
      )}

      {/* Caption card (690 §3). */}
      <div className="rounded-[14px] bg-slate-50 border border-slate-200 p-3.5" data-testid="cust-caption-card">
        <p className="text-[11px] font-black tracking-wide text-slate-400 uppercase">Your caption · tags included</p>
        {capBusy ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500" data-testid="engage-captions-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Getting caption ideas…
          </div>
        ) : (
          <p className="mt-1.5 text-xs text-slate-700 leading-relaxed" data-testid="cust-caption-text">
            {shownCaption || "Tap a platform and we'll copy a ready-made caption for you."}
          </p>
        )}
        <div className="mt-2.5 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid={photo?.b64 ? "cust-suggest-photo" : "cust-suggest-plain"}
            disabled={capBusy}
            onClick={fetchSuggestions}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-[11px] font-bold px-3 py-1.5 transition-colors disabled:opacity-60"
          >
            <Sparkles className="w-3 h-3" /> Suggest new
          </button>
          <button
            type="button"
            data-testid="cust-caption-copy"
            disabled={!shownCaption}
            onClick={() => copyCaption(shownCaption)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 text-[11px] font-bold px-3 py-1.5 transition-colors disabled:opacity-40"
          >
            {captionCopied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />} Copy
          </button>
        </div>
        {captions?.length > 1 && (
          <ul className="mt-2.5 space-y-1.5" data-testid="engage-share-caption-list">
            {captions.map((cap, i) => (
              <li key={i}>
                <button
                  type="button"
                  data-testid={`engage-share-caption-${i}`}
                  onClick={() => {
                    setCopiedIdx(i);
                    copyCaption(taggedCaption(cap, activeTagKey));
                  }}
                  className={`w-full text-left rounded-xl border px-3 py-2 text-[11px] leading-relaxed transition-colors ${
                    copiedIdx === i
                      ? "border-emerald-400 bg-emerald-50 text-slate-800"
                      : "border-slate-200 bg-white hover:border-slate-400 text-slate-600"
                  }`}
                >
                  {taggedCaption(cap, activeTagKey)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* SHARE ON — 2×2 grid, Google always 4th (688 / 690 §4 / 691). */}
      <div>
        <p className="text-[11px] font-black tracking-wide text-slate-400 uppercase mb-1.5">Share on</p>
        <div className="grid grid-cols-2 gap-2" data-testid="engage-share-cards">
          {SHARE_CARDS.map((c) => {
            const isUsed = used.has(c.key) || pInfo(c.key)?.available === false;
            const isSel = selected === c.key;
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`cust-share-${c.key}`}
                onClick={() => setSelected(c.key)}
                className={`rounded-[14px] border bg-white p-3 text-left transition-all hover:shadow-sm ${
                  isUsed ? "opacity-70" : ""
                }`}
                style={isSel ? { borderColor: TEAL, boxShadow: `0 0 0 1.5px ${TEAL}` } : { borderColor: "#ECECEC" }}
              >
                <TileGlyph k={c.key} used={isUsed} />
                <div className="mt-2 text-xs font-bold text-slate-900">
                  {c.key === "google" ? "⭐ Google review" : c.name}
                </div>
                <div className={`text-[10px] font-semibold ${isUsed ? "text-emerald-600" : isSel ? "font-bold" : "text-slate-400"}`} style={isSel && !isUsed ? { color: TEAL } : undefined}>
                  {isUsed
                    ? c.key === "google"
                      ? "✓ Reviewed"
                      : "✓ Shared"
                    : isSel
                    ? "Selected"
                    : "Opens the app"}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Primary — Open {Platform} (691 step 3, cust-open-platform). */}
      <button
        type="button"
        data-testid="cust-open-platform"
        onClick={openComposer}
        className="w-full min-h-[52px] rounded-full text-white text-sm font-black shadow-card transition-opacity hover:opacity-90"
        style={{ backgroundColor: TEAL }}
      >
        {selected === "google" ? "⭐ Open Google to review" : `Open ${platformName}`}
      </button>

      {/* Vendor pages — secondary links only (Iter-645). */}
      {(links.facebook || links.instagram || links.tiktok) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="engage-visit-links">
          {[["facebook", "Facebook"], ["instagram", "Instagram"], ["tiktok", "TikTok"]]
            .filter(([k]) => links[k])
            .map(([k, name]) => (
              <a
                key={k}
                href={links[k]}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`engage-visit-page-${k}`}
                className="text-[11px] font-bold text-slate-400 hover:text-slate-600 underline"
              >
                Visit their {name} page ↗
              </a>
            ))}
        </div>
      )}

      {/* Streak nudge / milestone (Iter-586). */}
      {streakQ.data?.milestone_hit ? (
        <div
          className="rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 text-white text-center px-4 py-4 shadow-card"
          data-testid="engage-streak-milestone"
        >
          <div className="text-lg font-black">🔥 {streakQ.data.posts_total} shared posts!</div>
          <div className="mt-0.5 text-[11px] font-semibold opacity-90">Milestone reached — you&apos;re a local legend</div>
        </div>
      ) : streakQ.data?.posts_total > 0 ? (
        <div
          className="flex items-center justify-center gap-1.5 rounded-xl bg-orange-50 border border-orange-200 px-3.5 py-2 text-xs font-bold text-orange-700"
          data-testid="engage-share-streak-nudge"
        >
          🔥 {streakQ.data.posts_total} post{streakQ.data.posts_total === 1 ? "" : "s"} shared
          {streakQ.data.posts_to_next > 0
            ? ` — only ${streakQ.data.posts_to_next} more to reach ${streakQ.data.next_milestone}!`
            : " — milestone reached!"}
        </div>
      ) : null}

      {footer}
    </div>
  );
}
